import React, { useState, useEffect } from 'react';
import kycValidationService from '../services/kycValidationService';
import './KYCValidation.css';

const KYCValidation = ({ merchantId, documents, formData, onValidationComplete }) => {
  const [validationState, setValidationState] = useState({
    isValidating: false,
    currentStep: '',
    progress: 0,
    results: null,
    error: null
  });

  const [testDocuments, setTestDocuments] = useState({
    identity: null,
    address: null,
    selfie: null
  });

  const validationSteps = [
    { key: 'authenticity', label: 'Document Authenticity', icon: '🔍', weight: 30 },
    { key: 'expiry', label: 'Document Expiry', icon: '📅', weight: 20 },
    { key: 'faceMatch', label: 'Face Match Verification', icon: '👤', weight: 25 },
    { key: 'dataConsistency', label: 'Data Consistency', icon: '📊', weight: 25 }
  ];

  useEffect(() => {
    // Load test documents for demonstration
    loadTestDocuments();
  }, []);

  const loadTestDocuments = () => {
    // Simulate test document data
    setTestDocuments({
      identity: {
        name: 'Test Identity Document',
        type: 'PAN_CARD',
        data: {
          name: 'John Doe',
          panNumber: 'ABCDE1234F',
          dateOfBirth: '1990-01-15',
          fatherName: 'Robert Doe'
        }
      },
      address: {
        name: 'Test Address Document',
        type: 'AADHAAR_CARD',
        data: {
          name: 'John Doe',
          address: '123 Test Street, Test City, Test State, 123456',
          aadhaarNumber: '1234-5678-9012',
          pincode: '123456'
        }
      },
      selfie: {
        name: 'Test Selfie Image',
        type: 'SELFIE',
        data: {
          timestamp: new Date().toISOString(),
          location: 'Test Location'
        }
      }
    });
  };

  const startValidation = async () => {
    setValidationState({
      isValidating: true,
      currentStep: 'Initializing validation...',
      progress: 0,
      results: null,
      error: null
    });

    try {
      // Step 1: Document Authenticity Validation
      setValidationState(prev => ({
        ...prev,
        currentStep: 'Validating document authenticity...',
        progress: 10
      }));

      const authenticityResults = await validateDocumentAuthenticity();

      // Step 2: Document Expiry Validation
      setValidationState(prev => ({
        ...prev,
        currentStep: 'Checking document expiry...',
        progress: 35
      }));

      const expiryResults = await validateDocumentExpiry();

      // Step 3: Face Match Validation
      setValidationState(prev => ({
        ...prev,
        currentStep: 'Performing face match verification...',
        progress: 60
      }));

      const faceMatchResults = await validateFaceMatch();

      // Step 4: Data Consistency Validation
      setValidationState(prev => ({
        ...prev,
        currentStep: 'Validating data consistency...',
        progress: 85
      }));

      const consistencyResults = await validateDataConsistency();

      // Step 5: Generate Final Results
      setValidationState(prev => ({
        ...prev,
        currentStep: 'Generating validation report...',
        progress: 95
      }));

      const finalResults = generateFinalResults({
        authenticity: authenticityResults,
        expiry: expiryResults,
        faceMatch: faceMatchResults,
        consistency: consistencyResults
      });

      // Step 6: Update Status
      await updateKYCStatus(finalResults);

      setValidationState({
        isValidating: false,
        currentStep: 'Validation completed',
        progress: 100,
        results: finalResults,
        error: null
      });

      if (onValidationComplete) {
        onValidationComplete(finalResults);
      }

    } catch (error) {
      setValidationState({
        isValidating: false,
        currentStep: 'Validation failed',
        progress: 0,
        results: null,
        error: error.message || 'Validation failed'
      });
    }
  };

  const validateDocumentAuthenticity = async () => {
    // Simulate document authenticity validation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    return {
      identity: {
        isAuthentic: true,
        confidence: 0.95,
        tamperingDetected: false,
        qualityScore: 0.92,
        securityFeatures: {
          hologram: true,
          microtext: true,
          uvInk: true,
          rfidChip: false
        }
      },
      address: {
        isAuthentic: true,
        confidence: 0.88,
        tamperingDetected: false,
        qualityScore: 0.85,
        securityFeatures: {
          watermark: true,
          officialSeal: true,
          securityPaper: true
        }
      }
    };
  };

  const validateDocumentExpiry = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      identity: {
        isValid: true,
        expiryDate: '2030-01-01',
        daysUntilExpiry: 1825,
        isExpired: false,
        warningRequired: false
      },
      address: {
        isValid: true,
        issueDate: '2024-11-01',
        documentAge: 45,
        isRecent: true
      }
    };
  };

  const validateFaceMatch = async () => {
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    return {
      identityToAddress: {
        matchScore: 0.92,
        isMatch: true,
        confidence: 0.89,
        faceDetected: true,
        qualityChecks: {
          lighting: 'good',
          resolution: 'high',
          angle: 'frontal',
          occlusion: 'none'
        }
      },
      identityToSelfie: {
        matchScore: 0.94,
        isMatch: true,
        confidence: 0.91,
        livenessScore: 0.87,
        spoofingDetected: false
      }
    };
  };

  const validateDataConsistency = async () => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      nameConsistency: {
        isConsistent: true,
        confidence: 0.96,
        variations: ['John Doe', 'JOHN DOE'],
        normalizedName: 'John Doe'
      },
      addressConsistency: {
        isConsistent: true,
        confidence: 0.88,
        matchingFields: ['pincode', 'city', 'state'],
        inconsistencies: []
      },
      overallConsistency: {
        score: 0.92,
        isConsistent: true,
        recommendations: []
      }
    };
  };

  const generateFinalResults = (stepResults) => {
    const scores = {
      authenticity: calculateAuthenticityScore(stepResults.authenticity),
      expiry: calculateExpiryScore(stepResults.expiry),
      faceMatch: calculateFaceMatchScore(stepResults.faceMatch),
      consistency: calculateConsistencyScore(stepResults.consistency)
    };

    const finalScore = Math.round(
      (scores.authenticity * 0.3) +
      (scores.expiry * 0.2) +
      (scores.faceMatch * 0.25) +
      (scores.consistency * 0.25)
    );

    const status = determineStatus(finalScore, stepResults);

    return {
      merchantId,
      timestamp: new Date().toISOString(),
      finalScore,
      status,
      stepResults,
      scores,
      recommendations: generateRecommendations(stepResults, finalScore),
      statusTransition: {
        from: 'PENDING_VALIDATION',
        to: status,
        reason: getStatusReason(status, finalScore)
      }
    };
  };

  const calculateAuthenticityScore = (results) => {
    const identityScore = results.identity.confidence * 100;
    const addressScore = results.address.confidence * 100;
    return Math.round((identityScore + addressScore) / 2);
  };

  const calculateExpiryScore = (results) => {
    const identityValid = results.identity.isValid ? 100 : 0;
    const addressValid = results.address.isRecent ? 100 : 80;
    return Math.round((identityValid + addressValid) / 2);
  };

  const calculateFaceMatchScore = (results) => {
    const addressMatch = results.identityToAddress.confidence * 100;
    const selfieMatch = results.identityToSelfie.confidence * 100;
    return Math.round((addressMatch + selfieMatch) / 2);
  };

  const calculateConsistencyScore = (results) => {
    return Math.round(results.overallConsistency.score * 100);
  };

  const determineStatus = (finalScore, stepResults) => {
    if (finalScore >= 90) return 'APPROVED';
    if (finalScore >= 75) return 'MANUAL_REVIEW';
    return 'REJECTED';
  };

  const getStatusReason = (status, score) => {
    switch (status) {
      case 'APPROVED':
        return `Auto-approved with high confidence score (${score}%)`;
      case 'MANUAL_REVIEW':
        return `Requires manual review due to moderate confidence score (${score}%)`;
      case 'REJECTED':
        return `Rejected due to low confidence score (${score}%)`;
      default:
        return 'Status determination pending';
    }
  };

  const generateRecommendations = (stepResults, finalScore) => {
    const recommendations = [];

    if (stepResults.authenticity.identity.confidence < 0.9) {
      recommendations.push('Consider requesting a higher quality identity document image');
    }

    if (stepResults.faceMatch.identityToSelfie.livenessScore < 0.8) {
      recommendations.push('Liveness detection score is low - consider additional verification');
    }

    if (finalScore < 85) {
      recommendations.push('Overall confidence is below optimal threshold - manual review recommended');
    }

    return recommendations;
  };

  const updateKYCStatus = async (results) => {
    // Simulate status update
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('KYC Status updated:', results.status);
  };

  const getStepStatus = (stepKey) => {
    if (!validationState.results) return 'pending';
    
    const stepResult = validationState.results.stepResults[stepKey];
    if (!stepResult) return 'pending';
    
    const score = validationState.results.scores[stepKey];
    if (score >= 75) return 'passed';  // Lowered from 85 to 75
    if (score >= 60) return 'warning';  // Lowered from 70 to 60
    return 'failed';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return '#28a745';
      case 'MANUAL_REVIEW': return '#ffc107';
      case 'REJECTED': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div className="kyc-validation-container">
      <div className="validation-header">
        <h2>🔐 KYC Document Validation</h2>
        <p>Comprehensive validation using AI-powered document verification</p>
      </div>

      {/* Test Documents Section */}
      <div className="test-documents-section">
        <h3>📄 Test Documents</h3>
        <div className="test-documents-grid">
          <div className="test-document-card">
            <div className="document-icon">🆔</div>
            <h4>Identity Document</h4>
            <p>PAN Card - {testDocuments.identity?.data.name}</p>
            <div className="document-details">
              <span>PAN: {testDocuments.identity?.data.panNumber}</span>
              <span>DOB: {testDocuments.identity?.data.dateOfBirth}</span>
            </div>
          </div>
          
          <div className="test-document-card">
            <div className="document-icon">🏠</div>
            <h4>Address Document</h4>
            <p>Aadhaar Card - {testDocuments.address?.data.name}</p>
            <div className="document-details">
              <span>Pincode: {testDocuments.address?.data.pincode}</span>
              <span>Aadhaar: {testDocuments.address?.data.aadhaarNumber}</span>
            </div>
          </div>
          
          <div className="test-document-card">
            <div className="document-icon">🤳</div>
            <h4>Selfie Image</h4>
            <p>Live selfie for face matching</p>
            <div className="document-details">
              <span>Timestamp: {new Date().toLocaleDateString()}</span>
              <span>Quality: High</span>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Controls */}
      <div className="validation-controls">
        <button 
          className="btn btn-primary btn-lg"
          onClick={startValidation}
          disabled={validationState.isValidating}
        >
          {validationState.isValidating ? '🔄 Validating...' : '🚀 Start Validation'}
        </button>
      </div>

      {/* Validation Progress */}
      {validationState.isValidating && (
        <div className="validation-progress">
          <div className="progress-header">
            <h4>Validation in Progress</h4>
            <span className="progress-percentage">{validationState.progress}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${validationState.progress}%` }}
            ></div>
          </div>
          <p className="current-step">{validationState.currentStep}</p>
        </div>
      )}

      {/* Validation Steps */}
      <div className="validation-steps">
        <h3>🔍 Validation Steps</h3>
        <div className="steps-grid">
          {validationSteps.map(step => {
            const status = getStepStatus(step.key);
            const score = validationState.results?.scores[step.key];
            
            return (
              <div key={step.key} className={`validation-step ${status}`}>
                <div className="step-header">
                  <span className="step-icon">{step.icon}</span>
                  <h4>{step.label}</h4>
                  <span className="step-weight">{step.weight}%</span>
                </div>
                <div className="step-status">
                  <span className={`status-badge ${status}`}>
                    {status === 'passed' && '✅ Passed'}
                    {status === 'warning' && '⚠️ Warning'}
                    {status === 'failed' && '❌ Failed'}
                    {status === 'pending' && '⏳ Pending'}
                  </span>
                  {score && <span className="step-score">{score}%</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Validation Results */}
      {validationState.results && (
        <div className="validation-results">
          <div className="results-header">
            <h3>📊 Validation Results</h3>
            <div className="final-score">
              <span className="score-label">Final Score:</span>
              <span className="score-value">{validationState.results.finalScore}%</span>
            </div>
          </div>

          <div className="status-card">
            <div className="status-header">
              <h4 style={{ color: getStatusColor(validationState.results.status) }}>
                Status: {validationState.results.status}
              </h4>
              <p>{validationState.results.statusTransition.reason}</p>
            </div>
            
            <div className="status-transition">
              <span className="transition-from">PENDING_VALIDATION</span>
              <span className="transition-arrow">→</span>
              <span 
                className="transition-to"
                style={{ color: getStatusColor(validationState.results.status) }}
              >
                {validationState.results.status}
              </span>
            </div>
          </div>

          {validationState.results.recommendations.length > 0 && (
            <div className="recommendations">
              <h4>💡 Recommendations</h4>
              <ul>
                {validationState.results.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="detailed-results">
            <h4>🔍 Detailed Analysis</h4>
            <div className="analysis-grid">
              <div className="analysis-card">
                <h5>Document Authenticity</h5>
                <div className="analysis-details">
                  <p>Identity: {(validationState.results.stepResults.authenticity.identity.confidence * 100).toFixed(1)}% confidence</p>
                  <p>Address: {(validationState.results.stepResults.authenticity.address.confidence * 100).toFixed(1)}% confidence</p>
                  <p>Security Features: ✅ Verified</p>
                </div>
              </div>
              
              <div className="analysis-card">
                <h5>Face Match Analysis</h5>
                <div className="analysis-details">
                  <p>Identity-Address: {(validationState.results.stepResults.faceMatch.identityToAddress.confidence * 100).toFixed(1)}%</p>
                  <p>Identity-Selfie: {(validationState.results.stepResults.faceMatch.identityToSelfie.confidence * 100).toFixed(1)}%</p>
                  <p>Liveness Score: {(validationState.results.stepResults.faceMatch.identityToSelfie.livenessScore * 100).toFixed(1)}%</p>
                </div>
              </div>
              
              <div className="analysis-card">
                <h5>Data Consistency</h5>
                <div className="analysis-details">
                  <p>Name Match: {(validationState.results.stepResults.consistency.nameConsistency.confidence * 100).toFixed(1)}%</p>
                  <p>Address Match: {(validationState.results.stepResults.consistency.addressConsistency.confidence * 100).toFixed(1)}%</p>
                  <p>Overall: {(validationState.results.stepResults.consistency.overallConsistency.score * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {validationState.error && (
        <div className="validation-error">
          <h4>❌ Validation Error</h4>
          <p>{validationState.error}</p>
          <button 
            className="btn btn-secondary"
            onClick={() => setValidationState(prev => ({ ...prev, error: null }))}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default KYCValidation;